import { useRef, useEffect, useCallback } from 'react'
import { X } from 'lucide-react'
import { useModal } from '../hooks/useModal'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  title?: string
  /** Custom max-width class (e.g., 'max-w-lg', 'max-w-2xl', 'max-w-5xl') */
  maxWidth?: string
  /** Whether to show the default header with title and close button */
  showHeader?: boolean
  /** Custom header content (overrides title) */
  headerContent?: React.ReactNode
  /** Additional classes for the modal content container */
  contentClassName?: string
  /** Whether clicking the backdrop closes the modal (default: true) */
  closeOnBackdropClick?: boolean
  /** Whether pressing Escape closes the modal (default: true) */
  closeOnEscape?: boolean
  /** Whether to enable focus trap (default: true) */
  trapFocus?: boolean
}

// Get all focusable elements within a container
function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const selector = [
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    'a[href]:not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"]):not([disabled])',
  ].join(', ')

  return Array.from(container.querySelectorAll<HTMLElement>(selector))
    .filter(el => el.offsetParent !== null) // Filter out hidden elements
}

export function Modal({
  isOpen,
  onClose,
  children,
  title,
  maxWidth = 'max-w-lg',
  showHeader = true,
  headerContent,
  contentClassName = '',
  closeOnBackdropClick = true,
  closeOnEscape = true,
  trapFocus = true,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null)
  const previousActiveElement = useRef<HTMLElement | null>(null)

  // Use the modal hook for escape key and body scroll prevention
  useModal({ isOpen, onClose, closeOnEscape })

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose()
    }
  }

  // Focus trap handler
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!trapFocus || !modalRef.current) return

    if (e.key === 'Tab') {
      const focusableElements = getFocusableElements(modalRef.current)
      if (focusableElements.length === 0) return

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (e.shiftKey) {
        // Shift + Tab - move backwards
        if (document.activeElement === firstElement) {
          e.preventDefault()
          lastElement.focus()
        }
      } else {
        // Tab - move forwards
        if (document.activeElement === lastElement) {
          e.preventDefault()
          firstElement.focus()
        }
      }
    }
  }, [trapFocus])

  // Store previous active element and focus first focusable element when modal opens
  useEffect(() => {
    if (isOpen) {
      // Store the currently focused element
      previousActiveElement.current = document.activeElement as HTMLElement

      // Focus the first focusable element in the modal
      if (modalRef.current) {
        const focusableElements = getFocusableElements(modalRef.current)
        if (focusableElements.length > 0) {
          // Slight delay to ensure DOM is fully rendered
          requestAnimationFrame(() => {
            focusableElements[0].focus()
          })
        }
      }

      // Add focus trap listener
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)

      // Restore focus to previous element when modal closes
      if (previousActiveElement.current && !isOpen) {
        previousActiveElement.current.focus()
      }
    }
  }, [isOpen, handleKeyDown])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div
        ref={modalRef}
        className={`bg-white rounded-xl shadow-xl w-full ${maxWidth} max-h-[90vh] overflow-auto ${contentClassName}`}
        role="document"
      >
        {showHeader && (title || headerContent) && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            {headerContent || (
              <h2 id="modal-title" className="text-lg font-semibold text-gray-900">
                {title}
              </h2>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
              aria-label="Close modal"
              type="button"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
        <div className={showHeader && (title || headerContent) ? 'p-6' : ''}>
          {children}
        </div>
      </div>
    </div>
  )
}

export default Modal
